<template>
    <div class="agent-editor-wrapper">
        <div class="header-section">
            <div class="back-button" @click="goBack">
                <i class="material-icons">arrow_back</i>
                <span>Geri</span>
            </div>
            <div class="title-wrapper">
                <h1 class="main-title">{{ agent ? agent.agent_name : 'Ses Ajanı' }}</h1>
            </div>
            <div class="metrics-wrapper" v-if="agent">
                <div class="metric-item" style="margin-right: 16px; position: relative;">
                    <a-tooltip placement="bottom">
                        <template slot="title">
                            <div><b>Fiyat (dakika başı)</b></div>
                            <div>Toplam: <b>${{ totalPricePerMin.toFixed(3) }}/dk</b></div>
                            <div style="margin-top: 6px;">
                                <div>LLM: ${{ llmPricePerMin.toFixed(3) }}/dk</div>
                                <div>Ses Modeli: $0.105/dk</div>
                                <div>Bilgi Tabanı: $0.0075/dk</div>
                            </div>
                        </template>
                        <span style="font-weight: 500; cursor: pointer;">
                            <i class="material-icons" style="vertical-align: middle; color: #1890ff;">attach_money</i>
                            <span style="vertical-align: middle;">${{ totalPricePerMin.toFixed(3) }}/dk</span>
                        </span>
                    </a-tooltip>
                </div>
                <div class="metric-item" style="position: relative;">
                    <a-tooltip placement="bottom">
                        <template slot="title">
                            <div><b>Gecikme (tahmini)</b></div>
                            <div>Toplam: <b>{{ totalLatencyText }}</b></div>
                            <div style="margin-top: 6px;">
                                <div>Ses Motoru: 450 ms</div>
                                <div>LLM: {{ llmLatencyText }}</div>
                                <div>Transkripsiyon: 100-300 ms</div>
                                <div>Konuşma Normalizasyonu: 100 ms</div>
                                <div>Bilgi Tabanı: 100-200 ms</div>
                            </div>
                        </template>
                        <span style="font-weight: 500; cursor: pointer;">
                            <i class="material-icons" style="vertical-align: middle; color: #faad14;">timer</i>
                            <span style="vertical-align: middle;">{{ totalLatencyText }}</span>
                        </span>
                    </a-tooltip>
                </div>
            </div>
        </div>

        <a-spin :spinning="loading">
            <div class="custom-tabs">
                <div class="tabs-header">
                    <div
                        v-for="tab in tabs"
                        :key="tab.key"
                        class="tab-item"
                        :class="{ active: currentTab === tab.key }"
                        @click="currentTab = tab.key"
                    >
                        <span>{{ tab.label }}</span>
                        <span v-if="tab.key === 'web-agent'" class="beta-ribbon">BETA</span>
                    </div>
                </div>
                <div class="tab-content">
                    <AgentTab v-if="currentTab === 'agent'" :agent="agent" @update="updateAgent" @llm-updated="handleLLMUpdated" />
                    <FunctionsTab v-if="currentTab === 'functions'" :agent="agent" @update="updateAgent" @llm-updated="handleLLMUpdated" />
                    <KnowledgeBaseTab v-if="currentTab === 'knowledge-base'" :agent="agent" @update="updateAgent" @llm-updated="handleLLMUpdated" />
                    <SpeechSettingsTab v-if="currentTab === 'speech-settings'" :agent="agent" @update="updateAgent" />
                    <CallSettingsTab v-if="currentTab === 'call-settings'" :agent="agent" @update="updateAgent" />
                    <SecurityFallbackSettingsTab v-if="currentTab === 'security-fallback'" :agent="agent" @update="updateAgent" @llm-updated="handleLLMUpdated" />
                    <WebAgentTab v-if="currentTab === 'web-agent'" :agent="agent" @update="updateAgent" />
                </div>
            </div>
        </a-spin>
    </div>
</template>

<script>
import { VOICE_AGENT_API } from "../../../../../../api/voiceAgent";
import AgentTab from "./tabs/AgentTab.vue";
import FunctionsTab from "./tabs/FunctionsTab.vue";
import KnowledgeBaseTab from "./tabs/KnowledgeBaseTab.vue";
import SpeechSettingsTab from "./tabs/SpeechSettingsTab.vue";
import CallSettingsTab from "./tabs/CallSettingsTab.vue";
import SecurityFallbackSettingsTab from "./tabs/SecurityFallbackSettingsTab.vue";
import WebAgentTab from "./tabs/WebAgentTab.vue";
import _ from "lodash";
import { mapState } from "vuex";

export default {
    name: "AgentEditor",
    components: {
        AgentTab,
        FunctionsTab,
        KnowledgeBaseTab,
        SpeechSettingsTab,
        CallSettingsTab,
        SecurityFallbackSettingsTab,
        WebAgentTab
    },
    props: {
        channelId: {
            type: String,
            required: true
        },
        agentId: {
            type: String,
            required: true
        },
        activeTab: {
            type: String,
            default: "agent"
        }
    },
    data() {
        return {
            loading: false,
            agent: null,
            currentTab: this.activeTab,
            tabs: [
                { key: 'agent', label: 'Agent' },
                { key: 'functions', label: 'Functions' },
                { key: 'knowledge-base', label: 'Knowledge Base' },
                { key: 'speech-settings', label: 'Speech Settings' },
                { key: 'call-settings', label: 'Call Settings' },
                { key: 'security-fallback', label: 'Security Fallback Settings' },
                { key: 'web-agent', label: 'Web Agent' }
            ],
            llmModelOptions: [
                { id: "gpt-5", defaultPrice: 0.04, fastPrice: 0.04 * 2, latency: "500-700 ms", hasFastTier: true },
                { id: "gpt-5-mini", defaultPrice: 0.012, fastPrice: 0.012 * 2, latency: "350-500 ms", hasFastTier: true },
                { id: "gpt-5-nano", defaultPrice: 0.003, fastPrice: null, latency: "300-450 ms", hasFastTier: false },
                { id: "gpt-4o_realtime", defaultPrice: 0.5, fastPrice: null, latency: "650-800 ms", hasFastTier: false },
                { id: "gpt-4o_mini_realtime", defaultPrice: 0.125, fastPrice: null, latency: "350-500 ms", hasFastTier: false },
                { id: "gpt-4o", defaultPrice: 0.05, fastPrice: 0.05 * 2, latency: "650-800 ms", hasFastTier: true },
                { id: "gpt-4o-mini", defaultPrice: 0.006, fastPrice: 0.006 * 2, latency: "350-500 ms", hasFastTier: true },
                { id: "gpt-4.1", defaultPrice: 0.045, fastPrice: 0.045 * 2, latency: "500-800 ms", hasFastTier: true },
                { id: "gpt-4.1-mini", defaultPrice: 0.016, fastPrice: 0.016 * 2, latency: "350-500 ms", hasFastTier: true },
                { id: "gpt-4.1-nano", defaultPrice: 0.006, fastPrice: 0.006 * 2, latency: "350-500 ms", hasFastTier: true },
                { id: "claude-4.5-sonnet", defaultPrice: 0.06, fastPrice: null, latency: "650-800 ms", hasFastTier: false },
                { id: "claude-3.5-haiku", defaultPrice: 0.02, fastPrice: null, latency: "350-500 ms", hasFastTier: false },
                { id: "gemini-2.5-flash", defaultPrice: 0.006, fastPrice: null, latency: "350-500 ms", hasFastTier: false },
                { id: "gemini-2.5-flash-lite", defaultPrice: 0.003, fastPrice: null, latency: "200-350 ms", hasFastTier: false },
            ],
        };
    },
    computed: {
        ...mapState({
            site: (state) => state.site.site,
        }),
        llmModelId() {
            if (!this.agent || !this.agent.llm) return null;
            // AgentTab'daki modelOptions ile uyumlu id'yi bulmak için
            return this.agent.llm.model;
        },
        llmModelInfo() {
            if (!this.llmModelId) return null;
            return this.llmModelOptions.find(m => m.id === this.llmModelId) || null;
        },
        aiCallCommission() {
            return this.site?.aiUsageCommission || 30;
        },
        rawLlmPricePerMin() {
            if (!this.llmModelInfo) return 0;

            // Fast tier kullanılıyor mu kontrol et
            const isFastTier = this.agent?.llm?.model_high_priority;

            if (isFastTier && this.llmModelInfo.hasFastTier && this.llmModelInfo.fastPrice) {
                return this.llmModelInfo.fastPrice;
            }

            return this.llmModelInfo.defaultPrice;
        },
        llmPricePerMin() {
            if (!this.rawLlmPricePerMin) return 0;
            const stableCommissionRate = 1.2;
            const aiCallCommissionRate = Math.max(this.aiCallCommission / 100, 0);
            const totalCommissionRate = stableCommissionRate + aiCallCommissionRate;
            const stableLlmPrice = this.rawLlmPricePerMin * stableCommissionRate;
            const totalLlmPrice = this.rawLlmPricePerMin * totalCommissionRate;
            return Math.max(totalLlmPrice, stableLlmPrice);
        },
        totalPricePerMin() {
            // LLM + Voice + Knowledge Base
            return this.llmPricePerMin + 0.105 + 0.0075;
        },
        llmLatencyText() {
            return this.llmModelInfo ? this.llmModelInfo.latency : "-";
        },
        totalLatencyText() {
            // Toplamı metin olarak gösteriyoruz, aralıkları topluyoruz
            if (!this.llmLatencyText || this.llmLatencyText === "-") return "-";
            // LLM aralığı ör: "650-800 ms" veya "200-350 ms"
            var llm = this.llmLatencyText.match(/(\d+)-(\d+)/);
            var llmMin = llm ? parseInt(llm[1], 10) : 0;
            var llmMax = llm ? parseInt(llm[2], 10) : 0;
            var min = 450 + llmMin + 100 + 100 + 100; // Voice + LLM + Trans + Norm + KB min
            var max = 450 + llmMax + 300 + 100 + 200; // Voice + LLM + Trans + Norm + KB max
            return `${min}-${max} ms`;
        }
    },
    watch: {
        currentTab(newTab) {
            // Tab değiştiğinde URL'deki query parametresini güncelle
            this.$router.push({
                path: this.$route.path,
                query: {
                    agent_id: this.agentId,
                    editor_tab: newTab
                }
            });
        },
        activeTab(newTab) {
            this.currentTab = newTab;
        }
    },
    methods: {
        async fetchAgent() {
            try {
                this.loading = true;
                const agent = await VOICE_AGENT_API.agents.get(this.agentId);
                this.agent = agent;
            } catch (error) {
                console.error("Ajan çekilirken hata oluştu:", error);
                this.$message.error("Ajan bilgileri yüklenirken bir hata oluştu");
            } finally {
                this.loading = false;
            }
        },
        updateAgent(updatedData) {
            this.loading = true;
            VOICE_AGENT_API.agents.update(this.agentId, updatedData)
                .then(updatedAgent => {
                    this.agent = updatedAgent;
                    this.$message.success("Ajan başarıyla güncellendi");
                })
                .catch(error => {
                    console.error("Ajan güncellenirken hata oluştu:", error);
                    this.$message.error("Ajan güncellenirken bir hata oluştu");
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        handleLLMUpdated(updatedLLM) {
            // LLM güncellendiğinde, agent nesnesini de güncelleyelim
            if (this.agent && this.agent.llm) {
                this.agent.llm = updatedLLM;
                this.$message.success("LLM promptu başarıyla güncellendi");
            }
        },
        goBack() {
            // Üst bileşene geri düğmesi tıklandığını bildir
            this.$emit('back');
        }
    },
    created() {
        this.fetchAgent();
    }
};
</script>

<style scoped lang="scss">
.agent-editor-wrapper {
    display: flex;
    flex-direction: column;
    padding: 0 20px;

    .header-section {
        display: flex;
        align-items: center;
        margin-bottom: 20px;
        .metrics-wrapper {
            display: flex;
            align-items: center;
            margin-left: auto;
        }
        .metric-item {
            font-size: 16px;
        }
        .back-button {
            display: flex;
            align-items: center;
            cursor: pointer;
            margin-right: 20px;
            color: #22272d;

            .material-icons {
                font-size: 20px;
                margin-right: 5px;
            }
        }

        .title-wrapper {
            display: flex;
            align-items: center;

            .main-title {
                font-size: 24px;
                font-weight: 500;
                margin: 0;
            }
        }
    }

    .custom-tabs {
        margin-top: 10px;
        border-radius: 8px;
        overflow: hidden;
        background: white;
        box-shadow: 0 1px 8px rgba(0, 0, 0, 0.08);

        .tabs-header {
            display: flex;
            align-items: center;
            border-bottom: 1px solid #f0f0f0;
            background: #fafafa;
            position: relative;

            .tab-item {
                padding: 14px 24px;
                cursor: pointer;
                position: relative;
                transition: all 0.3s;
                font-size: 15px;
                color: rgba(0, 0, 0, 0.65);

                &:hover {
                    color: #22272d;
                }

                &.active {
                    color: #22272d;
                    background: white;
                    border-bottom: none;
                    font-weight: 500;

                    &:after {
                        content: '';
                        position: absolute;
                        bottom: 0;
                        left: 0;
                        width: 100%;
                        height: 2px;
                        background: #22272d;
                    }
                }

                .beta-ribbon {
                    background-color: #ff4d4f;
                    color: #fff;
                    font-size: 12px;
                    padding: 2px 6px;
                    border-radius: 4px;
                    margin-left: 8px;
                    font-weight: bold;
                    position: relative;
                    top: -1px;
                }
            }
        }

        .tab-content {
            padding: 24px;
            min-height: 300px;
        }
    }
}
</style>
